import React from 'react';
import { Link } from 'react-router-dom';
import ResponsiveModal from '../common/ResponsiveModal';
import { formatCurrency } from '../../utils/formatters';

/**
 * PaymentsDrilldownModal
 * Summary of a project's payment posture without leaving the tab.
 * Shows the four payment buckets that add up to the row state, plus
 * a link to the full payment history on the site detail page.
 *
 * Props:
 *   isOpen, onClose
 *   row: project row from /analytics/payments/projects (nullable)
 */
export default function PaymentsDrilldownModal({ isOpen, onClose, row, siteLinkState }) {
  if (!row) return null;

  const lines = [
    { label: 'Milestone Paid', value: row.milestonePaid, tone: 'text-gray-900' },
    { label: 'VO Paid', value: row.voPaid, tone: 'text-gray-900' },
    { label: 'Deducted', value: row.totalDeducted, tone: row.totalDeducted > 0 ? 'text-red-600' : 'text-gray-400' },
    { label: 'Remaining', value: row.remaining, tone: 'text-gray-900 font-semibold' }
  ];

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Payments · ${row.projectNumber}`}
      size="md"
    >
      <div className="p-4 space-y-4">
        <div className="text-sm text-gray-500">{row.clientName || '—'}</div>

        <div className="bg-gray-50 rounded-md border border-gray-200 divide-y divide-gray-200">
          {lines.map(line => (
            <div key={line.label} className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-gray-600">{line.label}</span>
              <span className={`text-sm tabular-nums ${line.tone}`}>
                {formatCurrency(line.value || 0)}
              </span>
            </div>
          ))}
        </div>

        {row.hasActiveNotice && row.noticeIssuedAmount > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm">
            <span className="text-red-900 font-medium">Active notice: </span>
            <span className="text-red-700 tabular-nums">{formatCurrency(row.noticeIssuedAmount)}</span>
          </div>
        )}

        {row.nextMilestone && (
          <div className="text-sm text-gray-600">
            <span className="text-gray-400">Next milestone: </span>
            {row.nextMilestone.name}
          </div>
        )}

        {row.siteDatabaseId && (
          <div className="pt-2">
            <Link
              to={`/sites/${row.siteDatabaseId}`}
              state={siteLinkState}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
              onClick={onClose}
            >
              See full payment history →
            </Link>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
